import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { fetchResellerApplication, saveResellerApplication, submitResellerApplication, selfApplyReseller } from '../lib/api';

// V5.31 — publieke pagina achter de uitnodigingslink (?t=token).
// De kandidaat-handelaar vult hier zelf zijn bedrijfsgegevens in en dient
// in; Filip keurt daarna goed in zijn admin. Vóór die goedkeuring bestaat
// er geen account en is er niets zichtbaar van prijzen of klanten.
export default function ResellerAanvraagPage() {
  const token = new URLSearchParams(window.location.search).get('t') ?? '';
  const [status, setStatus] = useState<'laden' | 'zelf' | 'open' | 'submitted' | 'afgehandeld' | 'fout'>('laden');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [form, setForm] = useState<Record<string, string>>({
    company_name: '', contact_name: '', btw_number: '', contact_phone: '',
    contact_email: '', address: '', postal_code: '', city: '', website: '', type: '', notes: '',
  });

  // V6 punt 24 — zelf-aanmelding: zonder token toont de pagina een startvak
  // ('vraag uw korting aan'); na e-mail invullen gaat dezelfde invulflow open.
  const [startMail, setStartMail] = useState('');
  const [startBusy, setStartBusy] = useState(false);
  const startZelf = async () => {
    if (!/.+@.+\..+/.test(startMail.trim())) { setError('Geef een geldig e-mailadres.'); return; }
    setStartBusy(true); setError(null);
    try {
      const { token: t } = await selfApplyReseller(startMail.trim().toLowerCase());
      window.location.search = `?t=${t}`;
    } catch (e) { setError((e as Error).message); setStartBusy(false); }
  };

  useEffect(() => {
    if (!token) { setStatus('zelf'); return; }
    fetchResellerApplication(token)
      .then(app => {
        setEmail(app.email);
        setForm(f => {
          const next = { ...f };
          (Object.keys(f) as Array<keyof typeof f>).forEach(k => {
            const v = (app as unknown as Record<string, unknown>)[k];
            if (typeof v === 'string') next[k] = v;
          });
          return next;
        });
        if (app.status === 'approved' || app.status === 'rejected') setStatus('afgehandeld');
        else if (app.status === 'submitted') setStatus('submitted');
        else setStatus('open');
      })
      .catch(e => { setStatus('fout'); setError((e as Error).message); });
  }, [token]);

  const save = async (): Promise<boolean> => {
    setSaving(true); setError(null);
    try {
      const fields: Record<string, string> = {};
      Object.entries(form).forEach(([k, v]) => { if (v.trim()) fields[k] = v.trim(); });
      await saveResellerApplication(token, fields);
      setSavedAt(Date.now());
      return true;
    } catch (e) { setError((e as Error).message); return false; }
    finally { setSaving(false); }
  };

  const indienen = async () => {
    if (!(await save())) return;
    setSaving(true); setError(null);
    try {
      await submitResellerApplication(token);
      setStatus('submitted');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  const F = ({ label, k, type = 'text', placeholder = '', required = false }: {
    label: string; k: string; type?: string; placeholder?: string; required?: boolean;
  }) => (
    <div>
      <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <input
        type={type} value={form[k]} placeholder={placeholder}
        onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#c47a3a]"
      />
    </div>
  );

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#faf7f3]">
      <div className="max-w-2xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Playfair Display' }}>
          Handelaarsaccount aanvragen
        </h1>
        <p className="text-sm text-[#8a7e74] mt-2 mb-6">
          Vul hieronder uw bedrijfsgegevens in. Na het indienen bekijken wij uw aanvraag en
          krijgt u bericht zodra uw account met uw voorwaarden klaarstaat.
        </p>

        {status === 'laden' && (
          <div className="flex items-center gap-2 text-sm text-[#8a7e74]"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        )}

        {/* V6 punt 24 — zelf-aanmelding zonder uitnodiging */}
        {status === 'zelf' && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 sm:p-6">
            <p className="text-sm font-bold text-[#1a1a1a] mb-1">Vraag uw korting aan</p>
            <p className="text-sm text-[#8a7e74] mb-4">
              Bent u groothandel, kleinhandel, bouwonderneming of installateur? Start hier uw
              aanvraag: vul uw e-mailadres in en daarna uw bedrijfsgegevens. Uw BTW-nummer
              wordt automatisch gecontroleerd in het Europese VIES-register; wij keuren goed
              en kennen uw korting toe.
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                type="email" value={startMail} onChange={e => setStartMail(e.target.value)}
                placeholder="uw zakelijk e-mailadres"
                onKeyDown={e => { if (e.key === 'Enter') void startZelf(); }}
                className="flex-1 min-w-[220px] px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#c47a3a]"
              />
              <button onClick={() => void startZelf()} disabled={startBusy}
                className="px-4 py-2 bg-[#c47a3a] text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {startBusy ? 'Even geduld…' : 'Start mijn aanvraag'}
              </button>
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>
        )}

        {status === 'fout' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" /> {error ?? 'Deze uitnodiging is niet (meer) geldig.'}
          </div>
        )}

        {status === 'afgehandeld' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-sm text-green-800 flex items-start gap-2">
            <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
            Deze aanvraag is al afgehandeld. Probeer in te loggen of neem contact met ons op.
          </div>
        )}

        {status === 'submitted' && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-5 text-sm text-green-800 flex items-start gap-2">
            <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
            <span><b>Uw aanvraag is ingediend.</b> U krijgt bericht zodra ze is goedgekeurd.
            Iets vergeten? U kan hieronder nog aanvullen en opnieuw indienen.</span>
          </div>
        )}

        {(status === 'open' || status === 'submitted') && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 sm:p-6">
            <p className="text-xs text-[#8a7e74] mb-4">Uitnodiging voor: <b className="text-[#1a1a1a]">{email}</b></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Bedrijfsnaam" k="company_name" required />
              <F label="Contactpersoon" k="contact_name" required />
              <F label="BTW-nummer" k="btw_number" placeholder="BE0xxx.xxx.xxx" required />
              <F label="Telefoon" k="contact_phone" required />
              <F label="E-mail facturatie/contact" k="contact_email" type="email" placeholder={email} />
              <F label="Website" k="website" placeholder="https://…" />
              <div className="sm:col-span-2"><F label="Adres" k="address" /></div>
              <F label="Postcode" k="postal_code" />
              <F label="Gemeente" k="city" />
              <div>
                <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">Soort bedrijf</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
                  <option value="">— kies —</option>
                  <option value="groothandel">Groothandel</option>
                  <option value="kleinhandel">Kleinhandel</option>
                  <option value="bouwonderneming">Bouwonderneming / aannemer</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">Opmerkingen</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white resize-y" />
              </div>
            </div>
            <p className="text-xs text-[#8a7e74] mt-3">
              Uw logo en eventuele medewerkers voegen we toe zodra uw account is goedgekeurd.
            </p>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button onClick={() => void save()} disabled={saving}
                className="px-4 py-2 border border-[#c47a3a] text-[#c47a3a] rounded-lg text-sm font-semibold disabled:opacity-50">
                Tussentijds bewaren
              </button>
              <button onClick={() => void indienen()} disabled={saving}
                className="px-5 py-2 bg-[#c47a3a] text-white rounded-lg text-sm font-bold disabled:opacity-50">
                {saving ? 'Bezig…' : 'Aanvraag indienen'}
              </button>
              {savedAt && <span className="text-xs text-green-700">Bewaard ✓</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
